import videojs from 'video.js';

import AudioDescriptionsPlayer from './audio-descriptions/audio-descriptions-player.js';
import { QueryStringParamProvider } from '../../implementations/player-page/helpers.js';

const _audioPlayer = new WeakMap();
let _shouldEnableAudioDescription;
let localize;

/**
 * The AudioDescriptions module.
 * @member {AudioDescriptionsPlugin} audioDescriptions
 * @memberof MediasitePlayer#
 */

/**
 * Configures an audio descriptions track to play along with the main source video. Playback and audio volume are kept in sync with the main source video automatically.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class AudioDescriptionsPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        localize = player.localize?.bind(player);
        super(player, options);
    }

    /**
     * The source URL of an audio descriptions file to play along with the main media. If <code>null</code>, the existing audio descriptions track will be removed (if there is one).
     * @type {string}
     * @example
     * player.audioDescriptions.src = '/path/to/audio/file.mp3';
     */
    set src(src) {
        const player = this.player;
        localize = player.localize?.bind(player);
        let audioPlayer = _audioPlayer.get(this);
        const audioDescriptionsButton = player.getDescendant('ControlBar', 'AudioDescriptionsButton');

        if (src && src.length) {
            // Make sure there's an audio player component for the audio descriptions
            if (!audioPlayer) {
                audioPlayer = new AudioDescriptionsPlayer(player);
                audioPlayer.enabled = false;

                _audioPlayer.set(this, audioPlayer);
                player.addChild(audioPlayer);
            }

            audioPlayer.src = src;
            audioDescriptionsButton?.removeClass('vjs-hidden');
            this.enabled = audioDescriptionsButton.items.find(i => i.id() === 'enable_audio_descriptions')?.hasClass('vjs-selected');
        }
        else {
            this.removeAudioComponent(player);
            audioDescriptionsButton?.addClass('vjs-hidden');
        }

        const paramProvider = new QueryStringParamProvider(location.search);
        _shouldEnableAudioDescription = src && src.length && (paramProvider.getParamCaseSensitive("audiodesc") === "true");
        if (_shouldEnableAudioDescription) {
            audioDescriptionsButton.items.find(i => i.id() === 'main_audio_only')?.removeClass('vjs-selected');
            audioDescriptionsButton.items.find(i => i.id() === 'enable_audio_descriptions')?.addClass('vjs-selected');
            this.enabled = _shouldEnableAudioDescription;
        }
    }

    get src() {
        return _audioPlayer.get(this)?.src;
    }

    clear() {
        this.removeAudioComponent(this.player);
    }

    dispose() {
        this.removeAudioComponent(this.player);

        super.dispose();
    }

    get enabled() {
        return _audioPlayer.get(this)?.enabled ?? false;
    }

    set enabled(enabled) {
        const audioPlayer = _audioPlayer.get(this);
        audioPlayer.enabled = audioPlayer && audioPlayer.src ? enabled : false;
    }

    removeAudioComponent(player) {
        const audioPlayer = _audioPlayer.get(this);
        if (audioPlayer) {
            audioPlayer.src = null;
            player.removeChild(audioPlayer);
            _audioPlayer.delete(this);
        }
    }
}

export default AudioDescriptionsPlugin;
